import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { FaComment, FaTimes, FaPaperPlane } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

const SYSTEM_INSTRUCTION = `You are FSEL Assistant, a helpful and professional AI representative of 
Foundation Solar Energy Limited (FSEL) — a leading EPC solar company in 
Pakistan, part of the Fauji Foundation Group, established in 2019. 
You help website visitors with questions about FSEL's services, projects, 
leadership, and solar energy solutions. Answer based on the company data below.
You also have access to live Google Search. You must use it to fetch real-time information when the user asks about the latest solar news, articles, or renewable energy policies in Pakistan (e.g., NEPRA guidelines).
Answer only questions related to FSEL, solar energy, or energy policies. 
If asked anything unrelated, politely redirect the conversation back to FSEL. 
Be concise, professional, and up-to-date.

Data:
Company Name: Foundation Solar Energy Pvt Ltd
Parent Organization: Fauji Foundation
Founded (Renewables Entry): 2019
Industry: EPC Solar, Renewable Energy, Hybrid, Storage, Smart Energy
Website: www.fsel.com.pk
Email: contact@fsel.fonpv.org
Phone (Corporate): UAN: 051 111-113-735, 051 4720906 8, 0300 0503762, Fax: 051 4720908
Phone (Head Office): (051) 5951821
Address (Corporate): 1st Floor, Al Ghani Arcade, GT Road, Rawalpindi
Address (Head Office): Fauji Towers, Tipu Rd, Chaklala Cantt., Rawalpindi, 44000
Office Hours: Mon–Fri: 9:00 AM – 5:00 PM
Tagline: “From sun to socket, we deliver easy, reliable and green energy.”
Quotations: Users can request a free quotation by filling out the "Free Quotation" form on the "Home" or "Services" page (they need to enter their Full Name, Phone, Email, and Service Description). They can also reach out to the contact details provided.

🎯 Leadership & Governance
Chairman: Lt Gen Anwar Ali Hyder, HI (M), (Retd)
CEO / MD: Maj Gen Amjad Ahmed Butt, HI (M), (Retd) (Joined Aug 2024)
Company Secretary: Brig (R) Imran Kashfi
General Manager: Brig (R) Shahid Javed Hussain
Chief Financial Officer: Tughral Salman
Key Team Leads: Engr. Hammad Ahmed (Head of Ops), Taimur (Head of Business Dev), Hafiz Omer (Head of Design), Khizer Naveed (Lead Projects), Umar Bin Abdul Hameed (DM Tech).

📈 Growth, Portfolio & Performance
Impact: 50+ Satisfied Customers, 100+ MW Delivered, 25+ MW O&M Performed, 38.1 MW Power Purchase Agreements (PPAs).
Sectors Served & Notable Projects:
- FMCG: Rafhan Maize (6 MW), Pakistan Tobacco Company (0.6 MW), Fauji Foods (1 MW), Beacon Impex Textile (5.4 MW).
- Health: Eastridge Hospital (0.5 MW), FF Hospitals (Rwp, Peshawar, Lahore).
- Engineering: FAP Marine Terminal (1 MW), KSB Pumps (1 MW), FOTCO (1 MW), Frontier Paper Mills (1 MW).
- Education: Foundation University Rwp, FF Schools (Okara, Chakwal), APS Sargodha.
- Defence: Upper Punjab (14 MW), KPK (12 MW), Central Punjab (22.5 MW).

🔧 Services & Solutions
1. Industrial Solar: Utility-scale farms, Grid-tied, PPA models, Hybrid energy systems.
2. Commercial Solar: Rooftop installations, Net metering, Off-grid storage, corporate audits.
3. Design & Engineering: 3D Modeling/Simulation, custom architecture, site feasibility.
4. Microgrid & Hybrid Solutions: Demand charge reduction, Genset dispatch optimization, grid limitation management.
5. Battery Energy Storage System (BESS): Energy time-shifting, peak shaving, backup power, frequency regulation.
6. Operation & Maintenance (O&M): 24/7 remote monitoring via in-house NOC, preventive maintenance, system upgrades.
7. Advanced Tech: Automated Robotic Cleaning (works in high winds), Anti-Dust PV Panels (hydrophobic).

💼 Commercial Models
1. EPC (Self-Finance): Client pays upfront. Long-term savings. Full ownership.
2. Power Purchase Agreement (PPA): No upfront cost. FSEL invests. Client pays per unit. Better ROI (< 4 years), Energy cost approx PKR 5–6 / kWh.

🔄 Process (How We Work)
Step 01 - Survey: Analyzing energy needs and site feasibility.
Step 02 - Design: Technical design and system architecture (3D modeling, inverter sizing).
Step 03 - Procurement: Sourcing industry-leading, high-efficiency technology (Tier-1).
Step 04 - Deployment: Professional installation and grid integration.
Step 05 - O&M Support: Continuous 24/7 remote monitoring and operational maintenance.

🧩 Key FAQs
What does FSEL do? End-to-end EPC solar solutions including design, procurement, installation, and maintenance.
Does FSEL offer financing? Yes, via the PPA (Power Purchase Agreement) model (zero upfront cost).
What makes FSEL different? Backed by Fauji Foundation (70 yrs experience), strong leadership, nationwide presence, Tier-1 procurement, and advanced AI/robotic maintenance technologies.`;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hello! I am FSEL Assistant. How can I help you with your solar energy inquiries today?", sender: 'bot' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowTooltip(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  const [chatSession, setChatSession] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const initChat = async () => {
      try {
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY || "missing_key";
        const genAI = new GoogleGenerativeAI(apiKey);
        const model = genAI.getGenerativeModel({ 
          model: "gemini-2.5-flash",
          systemInstruction: SYSTEM_INSTRUCTION,
          tools: [{ googleSearch: {} }]
        });
        const session = model.startChat({
          history: [], 
          generationConfig: {
            maxOutputTokens: 4000,
          },
        });
        setChatSession(session);
      } catch (err) {
        console.error("Error initializing Gemini:", err);
      }
    };
    initChat();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim() || !chatSession) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
    setIsLoading(true);

    try {
      if (process.env.REACT_APP_GEMINI_API_KEY === "your_key_here" || !process.env.REACT_APP_GEMINI_API_KEY) {
        throw new Error("API key is not configured.");
      }
      const result = await chatSession.sendMessage(userMessage);
      let botResponse = result.response.text();
      botResponse = botResponse.replace(/\[cite:\s*[0-9,\s]*\]/g, (match) => {
        return match.replace('cite:', '').replace(/\s+/g, '');
      });

      try {
        const candidates = result.response.candidates;
        if (candidates && candidates.length > 0 && candidates[0].groundingMetadata) {
          const grounding = candidates[0].groundingMetadata;
          if (grounding.groundingChunks && grounding.groundingChunks.length > 0) {
            let sourcesList = "\n\n---\n**Sources:**\n";
            grounding.groundingChunks.forEach((chunk, index) => {
              if (chunk.web && chunk.web.uri) {
                const title = chunk.web.title || chunk.web.uri;
                sourcesList += `[${index + 1}] [${title}](${chunk.web.uri})\n`;
              }
            });
            botResponse += sourcesList;
          }
        }
      } catch (e) {
        console.error("Error parsing grounding data:", e);
      }

      setMessages(prev => [...prev, { text: botResponse, sender: 'bot' }]);
    } catch (error) {
      console.error("Chat Error:", error);
      
      let errorMessage = "I'm sorry, I'm having trouble connecting right now. Please check if the API key is configured correctly, or contact info@fsel.com.pk.";
      if (error && error.toString().includes("429")) {
        errorMessage = "Due to high traffic, FSEL Assistant is experiencing a brief delay. Please wait about a minute and try again, or you can contact our sales team directly at info@fsel.com.pk.";
      }
      
      setMessages(prev => [...prev, { 
        text: errorMessage, 
        sender: 'bot' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999]">
      {isOpen && (
        <div className="mb-4 bg-white rounded-xl shadow-2xl w-[320px] sm:w-[380px] flex flex-col overflow-hidden border border-gray-200" style={{ height: '500px' }}>
          <div className="bg-[#0a1628] text-white p-4 flex justify-between items-center shadow-md">
            <div>
              <h3 className="font-bold text-lg flex items-center gap-2">
                <span className="text-[#facc15]"><FaComment /></span>
                FSEL Assistant
              </h3>
              <p className="text-xs text-gray-300 font-medium">Foundation Solar Energy</p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-gray-300 hover:text-white transition-colors hover:bg-white/10 p-1 rounded-md"
            >
              <FaTimes size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 bg-gray-50 flex flex-col gap-4">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`max-w-[85%] rounded-2xl p-3 text-[14px] leading-relaxed flex flex-col shadow-sm ${
                  msg.sender === 'user' 
                    ? 'bg-[#0a1628] text-white self-end rounded-br-sm' 
                    : 'bg-white border border-gray-100 text-gray-800 self-start rounded-bl-sm'
                }`}
              >
                {msg.sender === 'user' ? (
                  msg.text
                ) : (
                  <ReactMarkdown 
                    components={{
                      p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                      li: ({node, ...props}) => <li className="mb-1" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-semibold" {...props} />
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="self-start bg-white border border-gray-100 text-gray-800 rounded-2xl rounded-bl-sm p-4 shadow-sm flex items-center gap-1.5">
                 <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                 <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                 <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 bg-white border-t border-gray-200 flex items-center gap-2 shadow-[0_-2px_10px_rgba(0,0,0,0.02)]">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Ask about our solar solutions..."
              className="flex-1 bg-gray-100 border border-transparent rounded-full px-4 py-2.5 text-sm focus:outline-none focus:bg-white focus:border-[#facc15] focus:ring-2 focus:ring-[#facc15]/20 transition-all font-medium"
              disabled={isLoading}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="bg-[#facc15] text-[#0a1628] p-2.5 rounded-full hover:bg-yellow-400 hover:scale-105 transition-all focus:outline-none disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed flex items-center justify-center w-10 h-10 shadow-sm"
              aria-label="Send message"
            >
              <FaPaperPlane className="ml-[-2px]" />
            </button>
          </div>
        </div>
      )}

      {!isOpen && (
        <div className="relative flex flex-col items-end">
          {showTooltip && (
            <div className="absolute bottom-[75px] right-0 mb-2 bg-white text-[#0a1628] px-4 py-3 rounded-xl shadow-[0_10px_25px_rgba(0,0,0,0.15)] border border-gray-200 text-sm font-medium whitespace-nowrap animate-bounce flex items-center gap-3 z-50">
              <span>Hey there! How may I help you?</span>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowTooltip(false); }} 
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <FaTimes size={12} />
              </button>
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-gray-200 transform rotate-45"></div>
            </div>
          )}
          <button
            onClick={() => { setIsOpen(true); setShowTooltip(false); }}
            className="bg-[#0a1628] hover:bg-[#0f213b] text-white p-4 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-105 active:scale-95 group relative z-10"
            aria-label="Open chat"
          >
            <FaComment size={26} className="text-[#facc15] group-hover:block transition-all" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
